// PowerSync local-first dispatch for `updateSpaceAlias` (FFR2 — field
// edit of an existing room's alias during capture).
//
// Flow:
//   1. Normalise the new alias (trim → blank/whitespace → null) with the
//      SAME rule the create-space path and the backend use, so the local
//      mirror and the authoritative server row converge byte-for-byte.
//   2. `UPDATE spaces SET alias = ?` locally. This is the ONLY local
//      UPDATE ever issued against the spaces table, which is the standing
//      invariant the connector's spaces-PATCH branch relies on: a
//      spaces-PATCH CRUD entry ⟺ an alias edit. The connector picks up
//      that PATCH and PUTs the dedicated alias endpoint with a partial
//      `{ alias }` body on reconnect (connector-dispatch-space-update.ts).
//   3. Emit an `alias-updated` event so the room list's secondary label
//      updates on the next reactive tick.
//
// Why only `alias` is written: re-pointing any OTHER spaces column locally
// (e.g. floor_id / room_type_id) would also generate a spaces-PATCH that
// the connector would mis-route to the alias endpoint — dropping the real
// change and corrupting the row. The whole feature leans on alias being
// the sole client-writable spaces column. See the connector branch comment.
//
// `updated_at` is client-side (SQLite now()); the Postgres updated-at
// trigger overwrites it with the server timestamp on upload. The client
// value is provisional.
//
// Idempotent + LWW-safe: a rename is last-write-wins at the row level
// (ADR-003). Re-running the same alias edit yields the same stored value;
// a concurrent rename from another device simply wins or loses by arrival
// order. There is no conflict payload to reconcile (unlike capture_items
// quantities), so the connector path is a simple PUT → success → emit,
// with a 404 idempotent ack if the room was deleted server-side.

import type {
  CaptureMutation,
  CaptureMutationResult,
} from './capture-client.types'
import { eventForResult } from './capture-client-events'
import { normaliseOptimisticAlias } from './capture-client-powersync-create-space'
import type { CaptureEventBus } from './capture-events'

import type { PowerSyncDb } from './capture-client-powersync'

export async function dispatchUpdateSpaceAlias(
  mutation: Extract<CaptureMutation, { kind: 'updateSpaceAlias' }>,
  db: PowerSyncDb,
  bus: CaptureEventBus,
): Promise<CaptureMutationResult> {
  // Normalise to the value the optimistic local row should carry — the
  // same trim + blank→null rule the backend applies, so the local mirror
  // matches the authoritative row that later arrives over the sync stream.
  const alias = normaliseOptimisticAlias(mutation.alias)

  // Optimistic local write. This generates a spaces-PATCH CRUD entry the
  // connector turns into a PUT against the dedicated alias endpoint. Only
  // `alias` (+ updated_at) is touched — see the file header for why
  // touching any other spaces column here is forbidden.
  await db.execute(
    `UPDATE spaces
       SET alias = ?,
           updated_at = strftime('%Y-%m-%dT%H:%M:%fZ','now')
     WHERE id = ?`,
    [alias, mutation.spaceId],
  )

  const result: CaptureMutationResult = {
    kind: 'updateSpaceAlias',
    spaceId: mutation.spaceId,
    alias,
  }
  bus.emit(eventForResult(mutation, result))
  return result
}
